import { Box, Flex, Switch, Text, VStack, useBoolean } from '@chakra-ui/react';
import { Mixpanel, QUIZZES_EDITED } from 'libs/third-party/Mixpanel';
import { memo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useWidget } from 'widgets/sdk';

import { ComponentWidgetIdKeys, MultipleChoicesWidgetData } from '../../MultipleChoicesWidget.types';
import { CorrectSection } from './CorrectSection';
import { MultipleChoicesSettingsTextInput } from './MultipleChoicesSettingsTextInput';

/**
 * Render the question text on the side panel settings
 */
const QuestionText = () => {
  const { componentWidgetIdMap } = useWidget<MultipleChoicesWidgetData>();
  const questionWidgetId = componentWidgetIdMap[ComponentWidgetIdKeys.QuestionText];

  return (
    <Box w='full'>
      <MultipleChoicesSettingsTextInput widgetId={questionWidgetId} />
    </Box>
  );
};

/**
 * Render the question section on the side panel settings
 */
export const QuestionSection = memo(() => {
  const { t } = useTranslation('editor_widget_toolbar', { keyPrefix: 'multipleChoicesToolbar', useSuspense: false });
  const { updateWidget, enableMultipleSelection, correctAnswers } = useWidget<MultipleChoicesWidgetData>();
  const [flag, setFlag] = useBoolean(enableMultipleSelection);

  // Toggle between Radio <-> Checkbox component
  const handleSwitchChange = useCallback(() => {
    // Parse the correctAnswers to swap Radio <-> Checkbox component
    const newCorrectAnswers = flag ? (correctAnswers.length ? [correctAnswers[0]] : ['0']) : correctAnswers;

    // Update side setting toolbar state
    updateWidget({
      enableMultipleSelection: !flag,
      correctAnswers: newCorrectAnswers,
    });

    setFlag.toggle();

    // Mixpanel event
    Mixpanel.track(QUIZZES_EDITED, { edit_type: !flag ? QUIZZES_EDITED.edit_type.on : QUIZZES_EDITED.edit_type.off });
  }, [flag, setFlag, correctAnswers, updateWidget]);

  useEffect(() => {
    if (enableMultipleSelection === flag) return;

    if (enableMultipleSelection) {
      setFlag.on();
    } else {
      setFlag.off();
    }
  }, [enableMultipleSelection, flag, setFlag]);
  return (
    <VStack>
      <QuestionText />
      <Flex w={'full'} textAlign={'left'} justifyContent='space-between'>
        <Text fontSize={'sm'} fontWeight='bold' my={2} textAlign={'left'}>
          {t('settingsSideBar.multipleChoices.correct')}
        </Text>
        <Flex flexDir='row' alignItems='center'>
          <Text fontSize='sm'>Multiple Selection</Text>
          <Switch size='md' pl={4} isChecked={flag} onChange={handleSwitchChange} />
        </Flex>
      </Flex>
      <CorrectSection isCheckbox={flag} />
    </VStack>
  );
});

QuestionSection.displayName = 'Question Section';

export default QuestionSection;
